// services/audio/clapDetector.js
//
// Интерфейс СОХРАНЁН: new ClapDetector({ threshold, minClapInterval, onClap }),
// start(), stop(), setListeningForClap(bool). Всё, что добавлено, —
// необязательное (ratio, confirmMs, decayRatio, speakerBoost, debug,
// setSpeakerActive). ScaleRunner, который их не передаёт, продолжит работать.
//
// ПОЧЕМУ ПЕРЕПИСАНО. Раньше «хлопком» считался ЛЮБОЙ кадр звука с громкостью
// (RMS) выше 0.05. С включёнными автоусилением (autoGainControl) и
// шумоподавлением (noiseSuppression) это давало ошибки в обе стороны:
//  - лишние хлопки: громкость тихой речи, кашля, щелчка мыши или звука самой
//    буквы из колонок (даже после эхоподавления) легко превышает 0.05, а
//    автоусиление ещё и подтягивает фон;
//  - пропущенные хлопки: первое же срабатывание на букву «съедало» её
//    единственную попытку (детектор отключается до следующей буквы), так что
//    посторонний шум в начале окна лишал настоящий хлопок возможности
//    сработать; а шумоподавление на части устройств приглушает хлопок как
//    «нестационарный шум».
//
// ТЕПЕРЬ хлопок — это звук, который одновременно:
//  1) громче абсолютного порога threshold (с запасом в speakerBoost раз,
//     пока из колонок звучит буква);
//  2) заметно громче текущего фона (в ratio раз) — порог подстраивается под
//     комнату и микрофон;
//  3) возник резко (громче предыдущего кадра минимум в 1.5 раза);
//  4) быстро затухает: через confirmMs мс громкость упала ниже
//     decayRatio от пика. Хлопок — импульс; речь, кашель и гул вентилятора
//     держатся дольше и отбрасываются.

export class ClapAnalyzer {
  constructor({
    threshold = 0.03,
    minClapInterval = 300,
    ratio = 4,
    confirmMs = 80,
    decayRatio = 0.5,
    speakerBoost = 2,
  } = {}) {
    this.threshold = threshold;
    this.minClapInterval = minClapInterval;
    this.ratio = ratio;
    this.confirmMs = confirmMs;
    this.decayRatio = decayRatio;
    this.speakerBoost = speakerBoost;

    this.floor = null;        // текущий уровень фонового шума
    this.prevRms = 0;
    this.candidate = null;    // возможный хлопок, ждёт проверки на затухание
    this.armed = false;       // «слушаем хлопок» (как listeningForClap раньше)
    this.cooldownUntil = 0;
    this.speakerActive = false;
    this.onReject = null;     // необязательный колбэк для отладки
  }

  setArmed(value, now) {
    this.armed = value;
    this.candidate = null;
    // Как и раньше: сразу после включения ждём minClapInterval мс
    if (value) this.cooldownUntil = now + this.minClapInterval;
  }

  setSpeakerActive(value) {
    this.speakerActive = value;
  }

  _updateFloor(rms) {
    if (this.floor === null) {
      this.floor = Math.max(rms, 0.002);
      return;
    }
    // Вверх фон «ползёт» медленно (громкий звук не должен задирать его),
    // вниз опускается быстро.
    const a = rms > this.floor ? 0.01 : 0.1;
    this.floor = Math.max(this.floor + (rms - this.floor) * a, 0.002);
  }

  // Вызывается на каждом кадре. Возвращает null или описание хлопка.
  process(rms, now) {
    // 1. Проверяем кандидата на затухание
    if (this.candidate) {
      const c = this.candidate;
      if (rms > c.peak) c.peak = rms;
      if (now - c.t < this.confirmMs) { this.prevRms = rms; return null; }

      this.candidate = null;
      this.prevRms = rms;
      if (rms < c.peak * this.decayRatio) {
        this.armed = false;                       // как раньше: один хлопок на окно буквы
        this.cooldownUntil = now + this.minClapInterval;
        return { volume: c.peak, floor: c.floor, ratio: c.peak / c.floor, t: c.t };
      }
      // Звук не затух — это не хлопок (речь, кашель, гул)
      this.cooldownUntil = now + this.minClapInterval;
      if (this.onReject) this.onReject({ volume: c.peak, floor: c.floor, reason: 'не затух' });
      return null;
    }

    const floor = this.floor === null ? Math.max(rms, 0.002) : this.floor;
    const thr = this.threshold * (this.speakerActive ? this.speakerBoost : 1);

    if (
      this.armed &&
      now >= this.cooldownUntil &&
      rms > thr &&
      rms > floor * this.ratio &&
      rms > this.prevRms * 1.5
    ) {
      this.candidate = { t: now, peak: rms, floor };
      this.prevRms = rms;
      return null;                                // фон не обновляем, пока идёт проверка
    }

    this._updateFloor(rms);
    this.prevRms = rms;
    return null;
  }
}

export class ClapDetector {
  constructor({
    threshold = 0.03,
    minClapInterval = 300,
    onClap = () => {},
    ratio,
    confirmMs,
    decayRatio,
    speakerBoost,
    debug = false,
  } = {}) {
    this.onClap = onClap;
    this.debug = debug;
    this.analyzer = new ClapAnalyzer({
      threshold, minClapInterval, ratio, confirmMs, decayRatio, speakerBoost,
    });
    if (debug) {
      this.analyzer.onReject = (r) =>
        console.log(`[CLAP] отброшен: ${r.reason}, громкость ${r.volume.toFixed(3)}, фон ${r.floor.toFixed(3)}`);
    }

    this.audioContext = null;
    this.stream = null;
    this.source = null;
    this.analyser = null;
    this.dataArray = null;
    this.animationFrame = null;
    this.running = false;
  }

  // Для совместимости со старым кодом, который читал это поле
  get listeningForClap() { return this.analyzer.armed; }

  async start() {
    if (this.running) return;

    // Эхоподавление оставляем (убирает звук колонок из микрофона).
    // Шумоподавление и автоусиление выключаем: они искажают и приглушают
    // резкие звуки — хлопок.
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: false,
        autoGainControl: false,
      },
    });

    this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    this.source = this.audioContext.createMediaStreamSource(this.stream);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 1024;
    this.analyser.smoothingTimeConstant = 0.05;
    this.dataArray = new Uint8Array(this.analyser.fftSize);
    this.source.connect(this.analyser);

    this.running = true;
    this.detect();
  }

  setListeningForClap(value) {
    this.analyzer.setArmed(value, performance.now());
  }

  // true — пока из колонок звучит буква (порог строже: звук буквы может
  // просачиваться в микрофон), false — в паузах
  setSpeakerActive(value) {
    this.analyzer.setSpeakerActive(value);
  }

  detect = () => {
    if (!this.running) return;

    this.analyser.getByteTimeDomainData(this.dataArray);
    let sum = 0;
    for (let i = 0; i < this.dataArray.length; i++) {
      const v = (this.dataArray[i] - 128) / 128;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / this.dataArray.length);

    const hit = this.analyzer.process(rms, performance.now());
    if (hit) {
      if (this.debug) {
        console.log(`[CLAP] хлопок: громкость ${hit.volume.toFixed(3)}, фон ${hit.floor.toFixed(3)}, в ${hit.ratio.toFixed(1)} раз громче фона`);
      }
      this.onClap({
        type: 'clap',
        timestamp: Date.now(),
        confidence: Math.min(hit.ratio / this.analyzer.ratio, 1),
        volume: hit.volume,
        floor: hit.floor,
      });
    }

    this.animationFrame = requestAnimationFrame(this.detect);
  };

  stop() {
    this.running = false;
    this.analyzer.armed = false;

    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.source = null;
    this.analyser = null;
    this.dataArray = null;
  }
}